import React, { useState, useRef, useEffect } from 'react';
import { FaPen, FaEraser, FaTrash, FaTimes, FaFont } from 'react-icons/fa';

const Whiteboard = ({ onClose }) => {
    const canvasRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [color, setColor] = useState('#000000');
    const [tool, setTool] = useState('pen'); // 'pen', 'eraser', 'text'
    const [textInput, setTextInput] = useState(null); // { x, y, value }

    // Initialize Canvas
    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        
        // Handle High DPI screens for sharp text
        const dpr = window.devicePixelRatio || 1;
        canvas.width = window.innerWidth * dpr;
        canvas.height = window.innerHeight * dpr;
        canvas.style.width = `${window.innerWidth}px`;
        canvas.style.height = `${window.innerHeight}px`;
        
        ctx.scale(dpr, dpr);
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.font = 'bold 24px Arial'; 
    }, []);

    // Update Context settings
    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        ctx.strokeStyle = tool === 'eraser' ? '#ffffff' : color;
        ctx.fillStyle = color;
        ctx.lineWidth = tool === 'eraser' ? 25 : 3;
    }, [color, tool]);

    const getCoordinates = (event) => {
        if (!canvasRef.current) return { x: 0, y: 0 };
        const rect = canvasRef.current.getBoundingClientRect();
        
        let clientX, clientY;
        if (event.touches && event.touches[0]) {
            clientX = event.touches[0].clientX;
            clientY = event.touches[0].clientY;
        } else {
            clientX = event.clientX;
            clientY = event.clientY;
        }

        return {
            x: clientX - rect.left,
            y: clientY - rect.top
        };
    };

    const startDrawing = (e) => {
        const { x, y } = getCoordinates(e);

        if (tool === 'text') {
            // If we are already typing, finish that text first
            if (textInput) {
                finalizeText();
            }
            // Spawn new text input
            setTextInput({ x, y, value: '' });
            return;
        }

        const ctx = canvasRef.current.getContext('2d');
        ctx.beginPath();
        ctx.moveTo(x, y);
        setIsDrawing(true);
    };

    const draw = (e) => {
        if (!isDrawing) return;
        const { x, y } = getCoordinates(e);
        const ctx = canvasRef.current.getContext('2d');
        ctx.lineTo(x, y);
        ctx.stroke();
    };

    const stopDrawing = () => {
        const ctx = canvasRef.current.getContext('2d');
        ctx.closePath();
        setIsDrawing(false);
    };

    const finalizeText = () => {
        if (!textInput || !textInput.value.trim()) {
            setTextInput(null);
            return;
        }
        const ctx = canvasRef.current.getContext('2d');
        ctx.fillStyle = color;
        ctx.font = 'bold 24px Arial';
        // Adjust y position slightly to match where the input was
        ctx.fillText(textInput.value, textInput.x, textInput.y + 18); 
        setTextInput(null);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') finalizeText();
    };

    const clearCanvas = () => {
        const ctx = canvasRef.current.getContext('2d');
        const canvas = canvasRef.current;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
    };

    return (
        <div className="fixed inset-0 z-[9999] bg-white animate-fade-in touch-none cursor-crosshair">
            {/* CANVAS */}
            <canvas
                ref={canvasRef}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
                className="w-full h-full block"
            />

            {/* TEXT INPUT OVERLAY */}
            {textInput && (
                <input
                    autoFocus
                    value={textInput.value}
                    onChange={(e) => setTextInput({ ...textInput, value: e.target.value })}
                    onBlur={finalizeText}
                    onKeyDown={handleKeyDown}
                    style={{
                        position: 'absolute',
                        left: textInput.x,
                        top: textInput.y - 10,
                        color: color,
                        fontSize: '24px',
                        fontFamily: 'Arial',
                        fontWeight: 'bold',
                        background: 'transparent',
                        border: '1px dashed #ccc',
                        outline: 'none',
                        minWidth: '100px',
                        zIndex: 10000
                    }}
                    placeholder="Type here..."
                />
            )}

            {/* TOOLBAR */}
            <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white px-6 py-3 rounded-full shadow-2xl flex items-center gap-6 border border-gray-700 pointer-events-auto">
                <button onClick={onClose} className="bg-red-500 hover:bg-red-600 p-2 rounded-full transition-colors" title="Close"><FaTimes /></button>
                <div className="w-px h-8 bg-gray-600 mx-2"></div>
                
                {/* Tools */}
                <button onClick={() => setTool('pen')} className={`p-2 rounded-lg transition ${tool === 'pen' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'}`} title="Pen"><FaPen /></button>
                <button onClick={() => setTool('text')} className={`p-2 rounded-lg transition ${tool === 'text' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'}`} title="Type Text"><FaFont /></button>

                {/* Colors */}
                <div className="flex gap-2">
                    {['#000000', '#EF4444', '#3B82F6', '#10B981'].map((c) => (
                        <button key={c} onClick={() => { setColor(c); setTool('pen'); }} className={`w-6 h-6 rounded-full border-2 ${color === c && tool !== 'eraser' ? 'border-white scale-125' : 'border-transparent hover:scale-110'} transition-transform`} style={{ backgroundColor: c }} />
                    ))}
                </div>

                <button onClick={() => setTool('eraser')} className={`p-2 rounded-lg transition ${tool === 'eraser' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white'}`} title="Eraser"><FaEraser /></button>
                <button onClick={clearCanvas} className="text-gray-400 hover:text-red-400 transition ml-2" title="Clear All"><FaTrash /></button>
            </div>
        </div>
    );
};

export default Whiteboard;